/// <reference path="geom.ts" />
;
export class Matrix2D {
    static isIdentity(m) {
        return m.a === 1 && m.d === 1 && m.b === 0 && m.f === 0 && m.c === 0 && m.e === 0;
    }
    static get identity() {
        return { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };
    }
    static copy(m) {
        return { a: m.a, b: m.b, c: m.c, d: m.d, e: m.e, f: m.f };
    }
    static scale(m, sx, sy = sx) {
        return { a: m.a * sx, b: m.b, c: m.c, d: m.d * sy, e: m.e, f: m.f };
    }
    static translate(m, t) {
        return { a: m.a, b: m.b, c: m.c, d: m.d, e: m.e + t.x, f: m.f + t.y };
    }
    static rotate(m, rad) {
        const cos = Math.cos(rad), sin = Math.sin(rad);
        return Matrix2D.multiply(m, { a: cos, b: sin, c: -sin, d: cos, e: 0, f: 0 });
    }
    static multiply(m1, m2) {
        if ("x" in m1 && "y" in m1) {
            if (typeof m2 === 'number') {
                return { x: m1.x * m2, y: m1.y * m2 };
            }
            else {
                return { x: m1.x * m2.a + m1.y * m2.c + m2.e, y: m1.x * m2.b + m1.y * m2.d + m2.f };
            }
        }
        if (typeof m2 === 'number') {
            return {
                a: m1.a * m2, b: m1.b * m2, c: m1.c * m2, d: m1.d * m2, e: m1.e * m2, f: m1.f * m2
            };
        }
        return {
            a: m1.a * m2.a + m1.b * m2.c,
            b: m1.a * m2.b + m1.b * m2.d,
            c: m1.c * m2.a + m1.d * m2.c,
            d: m1.c * m2.b + m1.d * m2.d,
            e: m1.e * m2.a + m1.f * m2.c + m2.e,
            f: m1.e * m2.b + m1.f * m2.d + m2.f
        };
    }
    static det(m) {
        return m.a * m.d - m.c * m.b;
    }
    static invert(m) {
        const det = this.det(m);
        if (det === 0) {
            // NaM(2D) ? 
            return null;
        }
        const invdet = 1 / det;
        /*
         * 1. swap a, d
         * 2. swap c, b and change signs
         * 3. e := -e*d + f*c
         * 4. f := e*b - f*a
         * 5. divide everything by [det]
        */
        return this.multiply({
            a: m.d,
            b: -m.b,
            c: -m.c,
            d: m.a,
            e: -m.e * m.d + m.c * m.f,
            f: m.e * m.b - m.a * m.f
        }, invdet);
    }
    static toComponents(m, transformOrigin = { x: 0, y: 0 }) {
        const scaleX = Math.sqrt(m.a * m.a + m.c * m.c);
        const scaleY = Math.sqrt(m.b * m.b + m.d * m.d);
        const cos = scaleX != 0 ? m.a / scaleX : 0;
        const sin = scaleX != 0 ? m.c / scaleX : 0;
        const rotation = Math.atan2(sin, cos);
        const { x: px, y: py } = transformOrigin;
        const translation = {
            x: m.e - px * (scaleX * cos - 1) + py * scaleX * sin,
            y: m.f - py * (scaleY * cos - 1) - px * scaleY * sin,
        };
        return {
            scaleX, scaleY, rotation,
            translation
        };
    }
    /**
     * Returns the matrix that transforms the triangle defined by the points p0, p1, p2 into the triangle defined by the points q0, q1, q2.
     * @param param0 Triangle defined by the points p0, p1, p2 (before transformation).
     * @param param1 Triangle defined by the points q0, q1, q2 (after transformation).
     * @returns Transformation matrix.
     */
    static fromTransform([p0, p1, p2], [q0, q1, q2]) {
        const det = p0.x * (p1.y - p2.y) -
            p0.y * (p1.x - p2.x) +
            (p1.x * p2.y - p2.x * p1.y);
        if (Math.abs(det) < 1e-10) {
            throw new Error("Points are collinear; matrix is singular and cannot be solved.");
        }
        const idet = 1 / det;
        // Matrix inversion of the 3x3 augmented source points
        // then multiplied by the destination points to get rows 1 and 2
        return {
            a: idet * (q0.x * (p1.y - p2.y) + q1.x * (p2.y - p0.y) + q2.x * (p0.y - p1.y)),
            b: idet * (q0.y * (p1.y - p2.y) + q1.y * (p2.y - p0.y) + q2.y * (p0.y - p1.y)),
            c: idet * (q0.x * (p2.x - p1.x) + q1.x * (p0.x - p2.x) + q2.x * (p1.x - p0.x)),
            d: idet * (q0.y * (p2.x - p1.x) + q1.y * (p0.x - p2.x) + q2.y * (p1.x - p0.x)),
            e: idet * (q0.x * (p1.x * p2.y - p2.x * p1.y) +
                q1.x * (p2.x * p0.y - p0.x * p2.y) +
                q2.x * (p0.x * p1.y - p1.x * p0.y)),
            f: idet * (q0.y * (p1.x * p2.y - p2.x * p1.y) +
                q1.y * (p2.x * p0.y - p0.x * p2.y) +
                q2.y * (p0.x * p1.y - p1.x * p0.y))
        };
    }
    /**
     * Returns whether two matrices are equal to each other, within a certain precision (number of decimal digits).
     * @param m1
     * @param m2
     * @param precision
     * @returns
     */
    static areClose(m1, m2, precision = 12) {
        return m1.a.isCloseTo(m2.a, precision)
            && m1.b.isCloseTo(m2.b, precision)
            && m1.c.isCloseTo(m2.c, precision)
            && m1.d.isCloseTo(m2.d, precision)
            && m1.e.isCloseTo(m2.e, precision)
            && m1.f.isCloseTo(m2.f, precision);
    }
}
// }
